'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import type { SearchBarProps } from './types';
import { SearchIcon, AiChatIcon } from './icons';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { useVoiceInput } from './useVoiceInput';
import VoiceMicButton from './VoiceMicButton';

// `onClear` is intentionally not destructured — the clear control is gone from
// the UI but the prop and its handler remain wired for an easy restore.
export default function SearchBar({ query, onChange, isLoading, onOpenChat, onVoiceQuery, selectAllSignal }: SearchBarProps) {
  const clientConfig = useClientConfig();
  const inputRef = useRef<HTMLInputElement>(null);

  // A scan that found nothing stays visible but selected, like a shop till:
  // the next scan replaces it rather than being appended to it. Only while the
  // box has focus — never steal focus from something else the shopper chose.
  //
  // React only to a CHANGE of the signal, never to its value at mount. There are
  // three SearchBars (entry screen + two results branches) and the first
  // keystroke swaps entry → results mid-word, mounting a fresh bar with that
  // letter in it. Reacting to the mount-time value (any earlier unmatched scan
  // left it > 0) selected that letter, so the second keystroke replaced it:
  // «nexium» → «exium», «depon» → «epon» (syfak, 2026-09-29 → 10-01; caused
  // by a6f659d, reproduced in a browser before the fix).
  const seenSelectAllSignal = useRef(selectAllSignal);
  useEffect(() => {
    if (selectAllSignal === seenSelectAllSignal.current) return;
    seenSelectAllSignal.current = selectAllSignal;
    if (!selectAllSignal) return;
    const input = inputRef.current;
    const root = input?.getRootNode() as Document | ShadowRoot | undefined;
    if (input && root?.activeElement === input) input.select();
  }, [selectAllSignal]);

  // ── Voice input ──────────────────────────────────────────────────────────
  // What is heard shows live in the box; nothing is searched until the shopper
  // stops talking, and then the words go through the ordinary search exactly
  // as if typed (decision a3b47b79 — no voice-specific search logic). A short
  // pause ends a search query; the chat input waits longer.
  const handleVoiceFinal = useCallback((heard: string) => {
    if (!onVoiceQuery) return;
    // Dictation can end a phrase with sentence punctuation («Ποτήρια κρασιού.»);
    // nobody types that into a search box.
    const query = heard.replace(/[.!?;…]+$/u, '').trim();
    if (!query) return;
    const root = inputRef.current?.getRootNode() as Document | ShadowRoot | undefined;
    // Commit now, so the search bar that replaces this one (entry screen →
    // results screen) has mounted before the focus decision below.
    flushSync(() => onVoiceQuery(query));
    if (window.matchMedia('(min-width: 768px)').matches) {
      // Leave the caret at the end so the shopper can correct a word.
      inputRef.current?.focus();
    } else {
      // On a phone, the new bar's `autoFocus` would pop the keyboard over the
      // results the shopper just asked for. Typing needs that autoFocus (the
      // screen swaps mid-word), a spoken search does not — undo it here.
      const active = root?.activeElement;
      if (active instanceof HTMLInputElement) active.blur();
    }
  }, [onVoiceQuery]);
  const voice = useVoiceInput({ silenceMs: 1200, onFinal: handleVoiceFinal });
  const showMic = voice.available && !!onVoiceQuery;
  const PLACEHOLDER_TEXTS = clientConfig.ui.strings.searchPlaceholder;
  const assistantName = clientConfig.ui.strings.aiAssistantTitle;
  // Relative avatar paths must load from our app, not the host page, when the
  // widget is embedded cross-origin (same rule as the product fallback image).
  const chatAvatarUrl = clientConfig.ui.chatAvatarUrl;
  const chatAvatarSrc = chatAvatarUrl
    ? chatAvatarUrl.startsWith('/')
      ? `${clientConfig.apiOrigin ?? ''}${chatAvatarUrl}`
      : chatAvatarUrl
    : undefined;
  const chatOpenLabel = clientConfig.ui.chatOpenLabel;
  // ── Placeholder format ───────────────────────────────────────────────────
  // Each entry may mark a FIXED opening with a single "|". Everything before
  // the bar is held static; only what follows is typed and erased:
  //
  //   'Αναζήτηση |προϊόντος'          → «Αναζήτηση » stays, tail cycles
  //   'Αναζήτηση |δραστικής ουσίας'
  //   'iPhone 16 θήκη'                → no bar: the whole line types and erases
  //
  // Mixing is fine — entries are independent, so a client can hold a prefix on
  // some lines and fully rewrite on others. No bar anywhere reproduces the
  // original behaviour exactly, which is why every existing client config is
  // unaffected. The value stays a plain string[], so nothing changes in the
  // config type, serialize.ts, or the DB read.
  const splitPlaceholder = (raw: string): { fixed: string; tail: string } => {
    const bar = raw.indexOf('|');
    if (bar === -1) return { fixed: '', tail: raw };
    return { fixed: raw.slice(0, bar), tail: raw.slice(bar + 1) };
  };

  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (query) {
      setDisplayedPlaceholder('');
      return;
    }

    const { fixed: staticPrefix, tail } = splitPlaceholder(PLACEHOLDER_TEXTS[placeholderIndex]);
    let charIndex = 0;

    if (isTyping) {
      intervalRef.current = setInterval(() => {
        charIndex++;
        setDisplayedPlaceholder(staticPrefix + tail.slice(0, charIndex));
        if (charIndex >= tail.length) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setTimeout(() => setIsTyping(false), 2000);
        }
      }, 60);
    } else {
      let eraseIndex = tail.length;
      intervalRef.current = setInterval(() => {
        eraseIndex--;
        setDisplayedPlaceholder(staticPrefix + tail.slice(0, eraseIndex));
        if (eraseIndex <= 0) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setPlaceholderIndex(prev => (prev + 1) % PLACEHOLDER_TEXTS.length);
          setIsTyping(true);
        }
      }, 30);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [placeholderIndex, isTyping, query]);

  return (
    <div className="relative flex flex-col justify-center items-start gap-[10px] self-stretch px-6">
      <div className="flex justify-between items-center gap-2 md:gap-3 self-stretch py-3 border-b border-black">
        <div className="flex items-center h-9 pl-3 gap-2 flex-1 min-w-0">
          <SearchIcon className="w-6 h-6 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            // While listening the box shows what is being heard (read-only);
            // the real query is untouched until the shopper stops talking.
            value={voice.listening ? voice.text : query}
            onChange={onChange}
            readOnly={voice.listening}
            // Clicking back into the box means "I'm done talking".
            onFocus={voice.listening ? voice.stop : undefined}
            placeholder={voice.listening ? voice.strings.listening : displayedPlaceholder}
            className="flex-1 min-w-0 text-[16px] leading-[19.5px] focus:outline-none bg-transparent font-gotham font-normal text-text-primary placeholder:text-text-secondary"
            autoFocus
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {showMic && (
            <VoiceMicButton
              listening={voice.listening}
              message={voice.message}
              onClick={voice.toggle}
              strings={voice.strings}
            />
          )}
        </div>
        {/* Loading spinner only. The clear control was removed entirely on
            2026-09-09 (founder's call) — it had been the English word "CLEAR"
            in an otherwise Greek UI, it competed for room with the chat
            launcher, and deleting the text by hand already returns the shopper
            to the entry screen (the empty-query branch in SearchWidget does the
            same reset). `onClear` is still declared and still passed, so the
            control can be restored without rebuilding `handleClearAll` — which
            aborts in-flight searches, records the query to recent searches,
            resets filters and closes the filter drawer. */}
        {isLoading && (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand flex-shrink-0" />
        )}
        {/* Chat launcher. A normal flex item at EVERY width.
            It was `md:absolute md:right-6` until 2026-09-09, which shared an
            exact right edge with the old CLEAR button — and once the launcher
            started rendering on results screens too (where CLEAR existed), the
            pill's solid background painted straight over it. CLEAR has since
            been removed entirely, so nothing collides today; in-flow is kept
            because it is simpler and cannot regress that way again.
            `md:py-0` keeps the pill the same 36px height as the h-9 input, so
            the row does not grow and the underline does not move.

            Below `md` the label is dropped and the control collapses to a
            compact brand-coloured square — the phone treatment in the Figma
            frames. It used to be `hidden md:flex`, i.e. no way at all to reach
            the assistant on a phone. */}
        {onOpenChat && (
          chatAvatarSrc ? (
            // Client-branded chat launcher (e.g. syfak's Αριάδνη) — the label
            // then the circular avatar. On a phone only the avatar survives, so
            // the pharmacist still sees who they are talking to.
            <button
              onClick={onOpenChat}
              aria-label={chatOpenLabel || `Άνοιγμα συνομιλίας${assistantName ? ` — ${assistantName}` : ''}`}
              className={`flex items-center flex-shrink-0 cursor-pointer bg-brand hover:opacity-90 transition-opacity p-1 ${chatOpenLabel ? 'md:gap-2.5 md:pl-4 md:pr-1.5 md:py-0' : 'md:p-0'}`}
            >
              {chatOpenLabel && (
                <span className="hidden md:inline text-[13px] font-gotham font-medium text-brand-text leading-tight whitespace-nowrap">
                  {chatOpenLabel}
                </span>
              )}
              <img
                src={chatAvatarSrc}
                alt={assistantName || 'Chat'}
                className="w-9 h-9 rounded-full object-cover bg-white flex-shrink-0"
              />
            </button>
          ) : (
            <button
              onClick={onOpenChat}
              aria-label={chatOpenLabel || `Άνοιγμα συνομιλίας${assistantName ? ` — ${assistantName}` : ''}`}
              className="flex items-center justify-center gap-0 md:gap-2.5 p-2.5 bg-brand hover:opacity-90 transition-colors text-[12px] font-gotham font-medium text-brand-text flex-shrink-0 cursor-pointer"
            >
              {/* 16px tall, like the sparkle it replaced, so the button keeps its height. */}
              <AiChatIcon className="w-4 h-4 flex-shrink-0" color="var(--color-brand-text)" />
              {/* Honours `ui.chatOpenLabel`, exactly as the avatar branch above
                  already does — until now this branch ignored it and printed the
                  literal below, so setting the label on a client without an
                  avatar did nothing at all.

                  The 'OPEN CHAT' fallback is DELIBERATE and stays English. A
                  hardcoded Greek default would be the same bug in another
                  language: every client today is Greek, but the first
                  non-Greek shop would get a Greek button, and unlike an English
                  string in a Greek shop nobody there could read it to notice.
                  The label belongs in each client's config — syfak already sets
                  «Ρωτήστε την Αριάδνη» and is the pattern to copy. */}
              <span className="hidden md:inline">{chatOpenLabel || 'OPEN CHAT'}</span>
            </button>
          )
        )}
      </div>
    </div>
  );
}
